"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import {
  ArrowRight,
  CheckCircle2,
  Download,
  Info,
  Loader2,
  Sparkles,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Slider } from "@/components/ui/slider";
import { StructuredData } from "@/components/common";
import MarketingConsent from "@/components/forms/MarketingConsent";
import {
  MARKETING_CONSENT_TEXT,
  appendMarketingConsent,
  withMarketingConsentLine,
} from "@/lib/marketing-consent";
import { trackEvent, trackDownload } from "@/lib/analytics";
import {
  defaultLocale,
  getLocalizedPathname,
  type Locale,
} from "@/lib/i18n";
import { whatsappHref } from "@/lib/whatsapp";
import EzyChatCard from "@/components/shared/EzyChatCard";
import GovernanceCallout from "@/components/ai-governance/GovernanceCallout";

const WHATSAPP = "601159246128";

// Display currencies for the SEA region. `perUsd` are indicative planning rates.
// VERIFIED 2026-08-11 against frankfurter.dev + open.er-api.com. Previous values were
// stale by up to 15% (MYR 4.7 -> 4.09, SGD 1.35 -> 1.28), which overstated every MYR/SGD
// estimate this tool produced. Re-verify these before quoting; see docs/research/.
// (this is a static-export tool with no live FX feed) — the on-page disclaimer
// tells users to confirm current rates. Symbols match the dropdown labels so the
// number formatting stays unambiguous (e.g. SGD shows "S$", not a bare "$").
type CurrencyCode = "USD" | "SGD" | "MYR" | "THB" | "IDR" | "PHP";
interface CurrencyInfo {
  label: string; // dropdown option text
  symbol: string; // prefix used for both on-page and Excel formatting
  locale: string; // Intl locale for digit grouping
  perUsd: number; // indicative units per 1 US dollar
  excelFmt: string; // ExcelJS numFmt pattern
}
const CURRENCIES: Record<CurrencyCode, CurrencyInfo> = {
  USD: { label: "USD — $", symbol: "$", locale: "en-US", perUsd: 1, excelFmt: '"$"#,##0' },
  SGD: { label: "SGD — S$", symbol: "S$", locale: "en-SG", perUsd: 1.28, excelFmt: '"S$" #,##0' },
  MYR: { label: "MYR — RM", symbol: "RM", locale: "ms-MY", perUsd: 4.09, excelFmt: '"RM" #,##0' },
  THB: { label: "THB — ฿", symbol: "฿", locale: "th-TH", perUsd: 33.1, excelFmt: '"฿"#,##0' },
  IDR: { label: "IDR — Rp", symbol: "Rp", locale: "id-ID", perUsd: 17838, excelFmt: '"Rp" #,##0' },
  PHP: { label: "PHP — ₱", symbol: "₱", locale: "en-PH", perUsd: 61.2, excelFmt: '"₱"#,##0' },
};
const CURRENCY_CODES = Object.keys(CURRENCIES) as CurrencyCode[];
const DEFAULT_CURRENCY: CurrencyCode = "MYR"; // matches the site's Malaysia focus
const RM_PER_USD = CURRENCIES.MYR.perUsd; // services input is quoted in RM

// Auto-detect the visitor's currency from their browser region. Timezone is more
// reliable than language for region, so try it first, then the language subtag.
const TZ_CURRENCY: Record<string, CurrencyCode> = {
  "Asia/Singapore": "SGD",
  "Asia/Kuala_Lumpur": "MYR",
  "Asia/Kuching": "MYR",
  "Asia/Bangkok": "THB",
  "Asia/Jakarta": "IDR",
  "Asia/Pontianak": "IDR",
  "Asia/Makassar": "IDR",
  "Asia/Jayapura": "IDR",
  "Asia/Manila": "PHP",
};
const REGION_CURRENCY: Record<string, CurrencyCode> = {
  SG: "SGD",
  MY: "MYR",
  TH: "THB",
  ID: "IDR",
  PH: "PHP",
  US: "USD",
};
function detectCurrency(): CurrencyCode | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && TZ_CURRENCY[tz]) return TZ_CURRENCY[tz];
    const langs =
      typeof navigator !== "undefined" && navigator.languages?.length
        ? navigator.languages
        : [typeof navigator !== "undefined" ? navigator.language : ""];
    for (const l of langs) {
      const region = l.split("-")[1]?.toUpperCase();
      if (region && REGION_CURRENCY[region]) return REGION_CURRENCY[region];
    }
  } catch {
    /* Intl / navigator unavailable — fall back to default */
  }
  return null;
}

// Team seat prices differ by billing cycle (from claude.com/pricing):
// annual commitment is cheaper per seat/month than paying monthly.
const TEAM_PRICES = {
  annual: { standard: 20, premium: 100 },
  monthly: { standard: 25, premium: 125 },
};

// Editable defaults (US$/seat/month) — from claude.com/pricing. Team seats bundle
// usage; Enterprise is a low (annual, negotiated) seat + metered usage at API rates.
const DEFAULTS = {
  standard: TEAM_PRICES.annual.standard, // Team Standard seat (annual)
  premium: TEAM_PRICES.annual.premium, // Team Premium seat (annual) — "5x usage"
  enterpriseSeat: 20, // Enterprise seat — PLUS metered API usage (below)
  servicesRM: 18000, // one-time deployment & enablement (RM)
};

// Estimated metered API usage on Enterprise (US$/user/month), by work profile
// and usage tier. Rough, editable industry estimates — coding/agentic work
// (Claude Code) consumes far more tokens than chat-based business operations.
// Grounded in Claude API rates (Haiku $1/$5, Sonnet $3/$15, Opus $5/$25 per MTok).
const API_DEFAULTS: Record<
  "ops" | "coding",
  { light: number; regular: number; heavy: number }
> = {
  ops: { light: 3, regular: 18, heavy: 60 },
  coding: { light: 60, regular: 300, heavy: 800 },
};

type Ui = {
  eyebrow: string;
  title: string;
  intro: string;
  credibility: string;
  /** In-text link to the "Claude partner Malaysia" owner page (2026-10-08). */
  partnerLead: string;
  partnerLink: string;
  partnerTail: string;
  inputsTitle: string;
  totalUsers: string;
  usageMix: string;
  usageHelp: string;
  low: string;
  average: string;
  heavy: string;
  lowHelp: string;
  avgHelp: string;
  heavyHelp: string;
  pricingTitle: string;
  pricingHelp: string;
  standard: string;
  premium: string;
  enterprise: string;
  perSeatMo: string;
  enterpriseNote: string;
  apiTitle: string;
  apiHelp: string;
  workProfile: string;
  opsLabel: string;
  codingLabel: string;
  profileHelp: string;
  seatWord: string;
  apiWord: string;
  billing: string;
  annual: string;
  monthly: string;
  annualNote: string;
  currency: string;
  services: string;
  servicesHelp: string;
  oneTimeRm: string;
  resultTitle: string;
  teamPlan: string;
  enterprisePlan: string;
  perMonth: string;
  perYear: string;
  threeYr: string;
  perUserMo: string;
  chartTitle: string;
  savingsLabel: string;
  recommendation: string;
  teamCheaper: (amt: string) => string;
  entCheaper: (amt: string) => string;
  teamWhy: string;
  entWhy: string;
  minSeats: string;
  download: string;
  downloading: string;
  gateTitle: string;
  gateDesc: string;
  companyLabel: string;
  emailLabel: string;
  generate: string;
  generating: string;
  privacy: string;
  consentLabel: string;
  privacyNotice: string;
  gateSuccess: string;
  gateSuccessDesc: string;
  gateError: string;
  downloadAgain: string;
  preparedFor: string;
  breakdownTitle: string;
  year: string;
  licenses: string;
  onboarding: string;
  total: string;
  assumptionsTitle: string;
  assumptions: string[];
  disclaimer: string;
  ctaTitle: string;
  ctaDesc: string;
  ctaBtn: string;
  /** Buy strip under the result cards, for companies that cannot pay Anthropic by card. */
  buyTitle: string;
  buyDesc: string;
  buyBtn: string;
  pricingBlog: string;
  /** The calculator answered "what will it cost"; this answers "who runs it". */
  managedService: string;
  agentCost: string;
  /**
   * Most people costing seats do not know Claude can reach their own systems.
   * This is the capability reveal, not a service pitch.
   */
  mcpLink: string;
  faqTitle: string;
  faqs: { q: string; a: string }[];
};

const UI: Record<Locale, Ui> = {
  en: {
    eyebrow: "Cost planning tool",
    title: "Claude Pricing Calculator: Team vs Enterprise Cost",
    intro:
      "See what Claude actually costs your organisation over three years. This Claude pricing calculator compares the Team and Enterprise plans side by side for your team size and usage — per seat plus API usage, in Ringgit, USD, SGD or your regional currency (THB, IDR, PHP) — and downloads an Excel breakdown for management.",
    credibility:
      "Built by Anchor Sprint, Certified in the Claude Partner Network.",
    partnerLead: "We are a ",
    partnerLink: "Claude partner in Malaysia",
    partnerTail: ".",
    inputsTitle: "Your organisation",
    totalUsers: "Total users",
    usageMix: "Usage mix",
    usageHelp:
      "Not everyone uses Claude the same way. Split your team by how heavily they'll use it — this decides how many need the higher-usage seats.",
    low: "Light",
    average: "Regular",
    heavy: "Heavy",
    lowHelp: "Occasional Q&A, drafting — a Standard seat is plenty.",
    avgHelp: "Daily use across tasks — a Standard seat still fits most.",
    heavyHelp: "All-day, long-context, power users — needs a Premium seat.",
    pricingTitle: "Per-seat pricing (US$/seat/month)",
    pricingHelp:
      "Pre-filled with current Claude for Work pricing — edit any field to match your quote.",
    standard: "Team Standard",
    premium: "Team Premium",
    enterprise: "Enterprise seat",
    perSeatMo: "/seat/mo",
    enterpriseNote:
      "Team seats bundle usage. Enterprise is a low seat price PLUS usage billed at API rates (set below). Custom-quoted — figures are editable estimates.",
    apiTitle: "Enterprise API usage (US$/user/month)",
    apiHelp:
      "On Enterprise, usage is metered at API rates on top of the seat. Estimated per usage tier — edit to your own numbers.",
    workProfile: "Work profile",
    opsLabel: "Business ops",
    codingLabel: "Coding / tech",
    profileHelp:
      "Coding & agentic work (Claude Code) consumes far more tokens than chat-based work — this presets the estimates.",
    seatWord: "Seat",
    apiWord: "API usage",
    billing: "Billing",
    annual: "Annual",
    monthly: "Monthly",
    annualNote:
      "Team seats change with the cycle — annual $20/$100, monthly $25/$125 per seat. Enterprise is billed annually plus metered API usage, so it stays the same here.",
    currency: "Currency",
    services: "Add deployment & enablement (Anchor Sprint)",
    servicesHelp:
      "Optional one-time rollout: setup, admin/SSO config, prompt training and change management.",
    oneTimeRm: "One-time (RM)",
    resultTitle: "Your 3-year total cost of ownership",
    teamPlan: "Team plan",
    enterprisePlan: "Enterprise plan",
    perMonth: "per month",
    perYear: "per year",
    threeYr: "3-year TCO",
    perUserMo: "per user / month",
    chartTitle: "Cumulative cost over 3 years",
    savingsLabel: "You save",
    recommendation: "Recommendation",
    teamCheaper: (a) =>
      `Team is about ${a} cheaper over 3 years at your usage mix.`,
    entCheaper: (a) =>
      `Enterprise is about ${a} cheaper over 3 years at your usage mix.`,
    teamWhy:
      "Most of your users fit Standard seats, so Team is the lean choice. Move to Enterprise when you need SSO/SCIM, advanced security & compliance, or heavy usage keeps growing.",
    entWhy:
      "With this many heavy users, buying Premium seats adds up — a flat Enterprise seat wins on cost and adds SSO, compliance and admin controls.",
    minSeats:
      "Note: Team runs from 2 to 150 seats; Enterprise is custom and usually has a minimum seat commitment. Confirm both with a quote.",
    download: "Download Excel breakdown",
    downloading: "Preparing…",
    gateTitle: "Get your company-branded Excel",
    gateDesc:
      "Add your company and work email — we'll generate a branded 3-year TCO you can send straight to management.",
    companyLabel: "Company name",
    emailLabel: "Work email",
    generate: "Generate my Excel",
    generating: "Generating…",
    privacy:
      "We'll only use your email to send the report, plus updates if you tick the box above. Figures are indicative — verify Anthropic's current pricing before purchase.",
    consentLabel: MARKETING_CONSENT_TEXT,
    privacyNotice: "Privacy Notice",
    gateSuccess: "Your branded Excel is downloading",
    gateSuccessDesc: "Didn't start?",
    gateError:
      "We couldn't send that just now. Please try again, or message us on WhatsApp and we'll send your report.",
    downloadAgain: "Download again",
    preparedFor: "Prepared for",
    breakdownTitle: "Year-by-year breakdown",
    year: "Year",
    licenses: "Licenses",
    onboarding: "Onboarding",
    total: "Total",
    assumptionsTitle: "How this is calculated",
    assumptions: [
      "Team plan: Light and Regular users on a Team Standard seat, Heavy users on a Team Premium seat — usage is bundled into the seat.",
      "Enterprise plan: a low flat seat PLUS metered usage billed at API rates (\"seat price + usage at API rates\"), estimated per user by usage tier and work profile.",
      "API estimates are rough industry figures — coding/agentic work (Claude Code) uses far more tokens than chat-based work — grounded in Claude's API rates and fully editable.",
      "Team monthly seats cost more than annual ($25/$125 vs $20/$100 per seat); Enterprise is annual + metered usage. Non-USD amounts use indicative FX rates — confirm current rates. Seat prices follow claude.com/pricing (confirm current figures).",
    ],
    disclaimer:
      "Indicative planning estimate only, not an official Anthropic quote. Actual pricing depends on seat count, usage and negotiation.",
    ctaTitle: "Want an exact Enterprise quote and a rollout plan?",
    ctaDesc:
      "We'll size the right plan, get you a real Enterprise quote, and plan the deployment — SSO, governance and enablement included.",
    ctaBtn: "Talk to our team",
    buyTitle: "Can't pay Anthropic by credit card?",
    buyDesc:
      "We help companies buy Claude Team or Enterprise on a purchase order, invoiced in RM or SGD. Paying by card? Buy direct from Anthropic.",
    buyBtn: "WhatsApp: I want to buy Claude",
    pricingBlog: "Read the full Claude pricing guide for Malaysia",
    managedService: "Or see who sets Claude up and keeps it running",
    agentCost: "This is the licence and API cost — see what building the agent costs",
    mcpLink: "Claude can also connect to the systems you already run (MCP)",
    faqTitle: "Questions teams ask",
    faqs: [
      {
        q: "How much does Claude cost per user in Malaysia?",
        a: "On the Team plan a Claude seat is about RM95 per user/month on annual billing (US$20; ~RM102 month-to-month), and a higher-usage Premium seat about RM470/month (US$100) — usage is bundled into the seat. On Enterprise you pay a low flat seat (~US$20) plus metered API usage, so cost per user tracks the work: a light business-ops user often lands near RM110/month, while a heavy coding/engineering user can run RM1,500–3,800/month at API rates. Set your user mix above to see your own per-user and 3-year figure in RM.",
      },
      {
        q: "What's the difference between Claude Team and Enterprise?",
        a: "Team is self-serve per-seat licensing for smaller groups (from 2 seats, up to 150) with Standard and higher-usage Premium seats — usage is bundled into the seat. Enterprise adds SSO/SCIM, advanced security & compliance, larger context and admin governance, and is priced differently: a low flat seat plus usage billed at API rates (\"seat price + usage at API rates\"), custom-quoted.",
      },
      {
        q: "Is this official Anthropic pricing?",
        a: "No. Team seat prices are pre-filled from current published rates, but every field is editable and the Enterprise figure is an editable planning estimate. Enterprise is always custom-quoted — talk to us for a real quote.",
      },
      {
        q: "How is the 3-year TCO calculated?",
        a: "For the Team plan we cost Light/Regular users on Standard seats and Heavy users on Premium seats (usage bundled). For Enterprise we add a low flat seat plus metered API usage, estimated per user by usage tier and work profile (business ops vs coding). Both are multiplied across 36 months, with an optional one-time onboarding line.",
      },
      {
        q: "Why does the usage mix matter?",
        a: "Heavy, all-day users can exceed a Standard seat's usage and need a Premium seat. The more heavy users you have, the more Premium seats you buy on Team — which is where a flat Enterprise seat often becomes cheaper.",
      },
      {
        q: "Can Anchor Sprint help us deploy Claude?",
        a: "Yes. We help size the right plan, secure an Enterprise quote, configure SSO and governance, and run prompt training and change management so adoption actually sticks.",
      },
    ],
  },
  ms: {
    eyebrow: "Alat perancangan kos",
    title: "Kalkulator Harga Claude: Kos Team vs Enterprise",
    intro:
      "Lihat kos sebenar Claude untuk organisasi anda selama tiga tahun. Kalkulator harga Claude ini membanding pelan Team dan Enterprise sebelah-menyebelah untuk saiz pasukan dan penggunaan anda — setiap kerusi serta penggunaan API, dalam Ringgit, USD, SGD atau mata wang serantau anda (THB, IDR, PHP) — dan memuat turun pecahan Excel untuk pengurusan.",
    credibility:
      "Dibina oleh Anchor Sprint, diperakui dalam Claude Partner Network.",
    partnerLead: "Kami ",
    partnerLink: "rakan kongsi Claude di Malaysia",
    partnerTail: ".",
    inputsTitle: "Organisasi anda",
    totalUsers: "Jumlah pengguna",
    usageMix: "Campuran penggunaan",
    usageHelp:
      "Tidak semua orang guna Claude sama. Bahagikan pasukan mengikut seberapa berat mereka menggunakannya — ini menentukan berapa ramai perlu kerusi penggunaan tinggi.",
    low: "Ringan",
    average: "Biasa",
    heavy: "Berat",
    lowHelp: "Soal jawab sekali-sekala, draf — kerusi Standard sudah cukup.",
    avgHelp: "Guna harian merentas tugas — kerusi Standard masih sesuai.",
    heavyHelp: "Sepanjang hari, konteks panjang — perlu kerusi Premium.",
    pricingTitle: "Harga setiap kerusi (US$/kerusi/bulan)",
    pricingHelp:
      "Diisi dengan harga Claude for Work semasa — sunting mana-mana medan mengikut sebut harga anda.",
    standard: "Team Standard",
    premium: "Team Premium",
    enterprise: "Kerusi Enterprise",
    perSeatMo: "/kerusi/bln",
    enterpriseNote:
      "Kerusi Team merangkumi penggunaan. Enterprise ialah harga kerusi rendah DITAMBAH penggunaan pada kadar API (tetapkan di bawah). Disebut harga tersuai — angka anggaran boleh sunting.",
    apiTitle: "Penggunaan API Enterprise (US$/pengguna/bulan)",
    apiHelp:
      "Pada Enterprise, penggunaan dimeter pada kadar API di atas kerusi. Anggaran setiap peringkat penggunaan — sunting ikut angka anda.",
    workProfile: "Profil kerja",
    opsLabel: "Operasi bisnes",
    codingLabel: "Pengekodan / teknikal",
    profileHelp:
      "Kerja pengekodan & agentik (Claude Code) guna lebih banyak token daripada kerja berasaskan chat — ini pratetapkan anggaran.",
    seatWord: "Kerusi",
    apiWord: "Penggunaan API",
    billing: "Pengebilan",
    annual: "Tahunan",
    monthly: "Bulanan",
    annualNote:
      "Kerusi Team berubah ikut kitaran — tahunan $20/$100, bulanan $25/$125 setiap kerusi. Enterprise dibil tahunan serta penggunaan API dimeter, jadi ia kekal sama di sini.",
    currency: "Mata wang",
    services: "Tambah pelaksanaan & pemboleh (Anchor Sprint)",
    servicesHelp:
      "Pelancaran sekali pilihan: persediaan, konfig admin/SSO, latihan prompt dan pengurusan perubahan.",
    oneTimeRm: "Sekali (RM)",
    resultTitle: "Jumlah kos pemilikan 3 tahun anda",
    teamPlan: "Pelan Team",
    enterprisePlan: "Pelan Enterprise",
    perMonth: "sebulan",
    perYear: "setahun",
    threeYr: "TCO 3 tahun",
    perUserMo: "setiap pengguna / bulan",
    chartTitle: "Kos terkumpul selama 3 tahun",
    savingsLabel: "Anda jimat",
    recommendation: "Cadangan",
    teamCheaper: (a) =>
      `Team lebih murah kira-kira ${a} selama 3 tahun pada campuran anda.`,
    entCheaper: (a) =>
      `Enterprise lebih murah kira-kira ${a} selama 3 tahun pada campuran anda.`,
    teamWhy:
      "Kebanyakan pengguna anda muat pada kerusi Standard, jadi Team pilihan jimat. Beralih ke Enterprise apabila anda perlukan SSO/SCIM, keselamatan & pematuhan lanjutan, atau penggunaan berat terus meningkat.",
    entWhy:
      "Dengan ramai pengguna berat, membeli kerusi Premium menjadi mahal — kerusi Enterprise rata menang dari segi kos serta menambah SSO, pematuhan dan kawalan admin.",
    minSeats:
      "Nota: Team bermula 5 kerusi; Enterprise tersuai dan biasanya ada komitmen kerusi minimum. Sahkan kedua-duanya dengan sebut harga.",
    download: "Muat turun pecahan Excel",
    downloading: "Menyediakan…",
    gateTitle: "Dapatkan Excel berjenama syarikat anda",
    gateDesc:
      "Masukkan syarikat dan e-mel kerja anda — kami jana TCO 3 tahun berjenama yang boleh terus dihantar kepada pengurusan.",
    companyLabel: "Nama syarikat",
    emailLabel: "E-mel kerja",
    generate: "Jana Excel saya",
    generating: "Menjana…",
    privacy:
      "Kami hanya guna e-mel anda untuk menghantar laporan, serta kemas kini jika anda tandakan kotak di atas. Angka anggaran — sahkan harga semasa Anthropic sebelum membeli.",
    consentLabel:
      "Ya, Anchor Sprint boleh menghantar e-mel kepada saya tentang acara, promosi dan produk baharu. Saya boleh berhenti melanggan pada bila-bila masa.",
    privacyNotice: "Notis Privasi",
    gateSuccess: "Excel berjenama anda sedang dimuat turun",
    gateSuccessDesc: "Tidak bermula?",
    gateError:
      "Kami tidak dapat menghantarnya sebentar tadi. Sila cuba lagi, atau hubungi kami di WhatsApp dan kami akan hantar laporan anda.",
    downloadAgain: "Muat turun semula",
    preparedFor: "Disediakan untuk",
    breakdownTitle: "Pecahan tahun demi tahun",
    year: "Tahun",
    licenses: "Lesen",
    onboarding: "Onboarding",
    total: "Jumlah",
    assumptionsTitle: "Cara ia dikira",
    assumptions: [
      "Pelan Team: pengguna Ringan dan Biasa pada kerusi Team Standard, pengguna Berat pada kerusi Team Premium — penggunaan dirangkum dalam kerusi.",
      "Pelan Enterprise: kerusi rata yang rendah DITAMBAH penggunaan dimeter pada kadar API (\"harga kerusi + penggunaan pada kadar API\"), dianggar setiap pengguna mengikut peringkat penggunaan dan profil kerja.",
      "Anggaran API ialah angka industri kasar — kerja pengekodan/agentik (Claude Code) guna lebih banyak token daripada kerja berasaskan chat — berdasarkan kadar API Claude dan boleh disunting.",
      "Kerusi Team bulanan lebih mahal daripada tahunan ($25/$125 vs $20/$100 setiap kerusi); Enterprise ialah tahunan + penggunaan dimeter. Jumlah bukan USD guna kadar tukaran indikatif — sahkan kadar semasa. Harga kerusi mengikut claude.com/pricing (sahkan angka semasa).",
    ],
    disclaimer:
      "Anggaran perancangan sahaja, bukan sebut harga rasmi Anthropic. Harga sebenar bergantung pada bilangan kerusi, penggunaan dan rundingan.",
    ctaTitle: "Mahu sebut harga Enterprise tepat dan pelan pelancaran?",
    ctaDesc:
      "Kami akan saiz pelan yang betul, dapatkan sebut harga Enterprise sebenar, dan rancang pelaksanaan — SSO, tadbir urus dan pemboleh disertakan.",
    ctaBtn: "Hubungi pasukan kami",
    buyTitle: "Tidak boleh membayar Anthropic dengan kad kredit?",
    buyDesc:
      "Kami membantu syarikat membeli Claude Team atau Enterprise melalui pesanan belian, dengan invois dalam RM atau SGD. Membayar dengan kad? Beli terus daripada Anthropic.",
    buyBtn: "WhatsApp: Saya mahu beli Claude",
    pricingBlog: "Baca panduan penuh harga Claude untuk Malaysia",
    managedService: "Atau lihat siapa yang memulakan Claude dan mengekalkannya",
    agentCost: "Ini kos lesen dan API — lihat pula kos membina ejennya",
    mcpLink: "Claude juga boleh disambungkan ke sistem yang anda sudah guna (MCP)",
    faqTitle: "Soalan yang pasukan tanya",
    faqs: [
      {
        q: "Berapa kos Claude setiap pengguna di Malaysia?",
        a: "Pada pelan Team, satu kerusi Claude kira-kira RM95 sepengguna/bulan pada bil tahunan (US$20; ~RM102 bulan-ke-bulan), dan kerusi Premium penggunaan tinggi kira-kira RM470/bulan (US$100) — penggunaan dirangkum dalam kerusi. Pada Enterprise anda bayar kerusi rata rendah (~US$20) serta penggunaan API dimeter, jadi kos sepengguna ikut jenis kerja: pengguna operasi bisnes ringan selalunya sekitar RM110/bulan, manakala pengguna pengekodan berat boleh mencecah RM1,500–3,800/bulan pada kadar API. Tetapkan campuran pengguna di atas untuk lihat angka sepengguna dan 3 tahun anda dalam RM.",
      },
      {
        q: "Apa beza Claude Team dan Enterprise?",
        a: "Team ialah pelesenan setiap kerusi layan-diri untuk kumpulan lebih kecil (dari 5 kerusi), dengan kerusi Standard dan Premium penggunaan tinggi. Enterprise menambah SSO/SCIM, keselamatan & pematuhan lanjutan, had penggunaan lebih tinggi, konteks lebih besar dan tadbir urus admin — dengan harga tersuai dirunding.",
      },
      {
        q: "Adakah ini harga rasmi Anthropic?",
        a: "Tidak. Harga kerusi Team diisi daripada kadar terbitan semasa, tetapi setiap medan boleh disunting dan angka Enterprise ialah anggaran perancangan. Enterprise sentiasa disebut harga tersuai — hubungi kami untuk sebut harga sebenar.",
      },
      {
        q: "Bagaimana TCO 3 tahun dikira?",
        a: "Untuk pelan Team kami kira pengguna Ringan/Biasa pada kerusi Standard dan pengguna Berat pada kerusi Premium (penggunaan dirangkum). Untuk Enterprise kami tambah kerusi rata rendah serta penggunaan API dimeter, dianggar setiap pengguna mengikut peringkat penggunaan dan profil kerja (operasi bisnes vs pengekodan). Kedua-duanya didarab 36 bulan, dengan baris onboarding sekali pilihan.",
      },
      {
        q: "Kenapa campuran penggunaan penting?",
        a: "Pengguna berat sepanjang hari boleh melebihi penggunaan kerusi Standard dan perlukan kerusi Premium. Semakin ramai pengguna berat, semakin banyak kerusi Premium dibeli pada Team — di situlah kerusi Enterprise rata sering jadi lebih murah.",
      },
      {
        q: "Boleh Anchor Sprint bantu kami laksana Claude?",
        a: "Ya. Kami bantu saiz pelan yang betul, dapatkan sebut harga Enterprise, konfig SSO dan tadbir urus, serta jalankan latihan prompt dan pengurusan perubahan supaya penggunaan benar-benar kekal.",
      },
    ],
  },
  zh: {
    eyebrow: "成本规划工具",
    title: "Claude 定价计算器：Team 与 Enterprise 成本",
    intro:
      "看看 Claude 三年内对你组织的真实成本。这个 Claude 定价计算器并排比较 Team 与 Enterprise 方案，按你的团队规模与用量——每席位加 API 用量，以令吉或美元显示——并下载 Excel 明细呈交管理层。",
    credibility: "由 Anchor Sprint 构建——Claude Partner Network 认证合作伙伴。",
    partnerLead: "我们是",
    partnerLink: "马来西亚的 Claude 合作伙伴",
    partnerTail: "。",
    inputsTitle: "你的组织",
    totalUsers: "总用户数",
    usageMix: "使用强度分布",
    usageHelp:
      "不是每个人用 Claude 的方式都一样。按使用强度划分团队——这决定了多少人需要更高用量的席位。",
    low: "轻度",
    average: "常规",
    heavy: "重度",
    lowHelp: "偶尔问答、起草——Standard 席位足矣。",
    avgHelp: "每日跨任务使用——Standard 席位仍适合多数人。",
    heavyHelp: "全天、长上下文的重度用户——需要 Premium 席位。",
    pricingTitle: "每席位价格（美元/席位/月）",
    pricingHelp: "已按当前 Claude for Work 价格预填——可编辑任意字段以匹配你的报价。",
    standard: "Team Standard",
    premium: "Team Premium",
    enterprise: "Enterprise 席位",
    perSeatMo: "/席位/月",
    enterpriseNote:
      "Team 席位含用量。Enterprise 是较低的席位价，另加按 API 费率计费的用量（在下方设置）。定制报价——数字为可编辑估算。",
    apiTitle: "Enterprise API 用量（美元/用户/月）",
    apiHelp:
      "在 Enterprise 上，用量在席位之外按 API 费率计费。按使用强度估算——可按你的实际数字编辑。",
    workProfile: "工作画像",
    opsLabel: "业务运营",
    codingLabel: "编码 / 技术",
    profileHelp:
      "编码与智能体工作（Claude Code）消耗的 token 远多于聊天类工作——这会预设估算值。",
    seatWord: "席位",
    apiWord: "API 用量",
    billing: "计费",
    annual: "年付",
    monthly: "月付",
    annualNote:
      "Team 席位随计费周期变化——年付 $20/$100，月付 $25/$125（每席位）。Enterprise 按年计费另加按量 API 用量，因此此处不变。非美元金额使用指示性汇率，请以当前汇率为准。",
    currency: "货币",
    services: "加入部署与赋能（Anchor Sprint）",
    servicesHelp: "可选的一次性落地：搭建、管理员/SSO 配置、提示培训与变革管理。",
    oneTimeRm: "一次性（RM）",
    resultTitle: "你的三年总拥有成本",
    teamPlan: "Team 方案",
    enterprisePlan: "Enterprise 方案",
    perMonth: "每月",
    perYear: "每年",
    threeYr: "三年 TCO",
    perUserMo: "每用户 / 月",
    chartTitle: "三年累计成本",
    savingsLabel: "你节省",
    recommendation: "建议",
    teamCheaper: (a) => `在你的使用分布下，Team 三年约便宜 ${a}。`,
    entCheaper: (a) => `在你的使用分布下，Enterprise 三年约便宜 ${a}。`,
    teamWhy:
      "你的多数用户适合 Standard 席位，因此 Team 更精简。当你需要 SSO/SCIM、进阶安全与合规，或重度使用持续增长时，再转向 Enterprise。",
    entWhy:
      "重度用户这么多时，购买 Premium 席位成本累加——统一的 Enterprise 席位在成本上更划算，还附带 SSO、合规与管理控制。",
    minSeats:
      "注：Team 起步 5 席位；Enterprise 为定制，通常有最低席位承诺。两者请以报价为准。",
    download: "下载 Excel 明细",
    downloading: "准备中…",
    gateTitle: "获取带公司品牌的 Excel",
    gateDesc:
      "填写你的公司与工作邮箱——我们会生成一份带品牌的三年 TCO，可直接呈交管理层。",
    companyLabel: "公司名称",
    emailLabel: "工作邮箱",
    generate: "生成我的 Excel",
    generating: "生成中…",
    privacy:
      "我们仅用你的邮箱发送报告；若你勾选上方的方框，也会发送最新资讯。数字为估算——购买前请核对 Anthropic 当前价格。",
    consentLabel:
      "是的，我同意安克极速通过电子邮件向我发送活动、促销和新产品信息。我可随时取消订阅。",
    privacyNotice: "隐私声明",
    gateSuccess: "带品牌的 Excel 正在下载",
    gateSuccessDesc: "没有开始？",
    gateError:
      "刚才未能发送。请重试，或通过 WhatsApp 联系我们，我们会把报告发给你。",
    downloadAgain: "重新下载",
    preparedFor: "呈送",
    breakdownTitle: "逐年明细",
    year: "年份",
    licenses: "许可",
    onboarding: "落地",
    total: "合计",
    assumptionsTitle: "计算方式",
    assumptions: [
      "Team 方案：轻度与常规用户按 Team Standard 席位、重度用户按 Team Premium 席位计价——用量已含在席位内。",
      "Enterprise 方案：较低的统一席位价，另加按 API 费率计费的用量（“席位价 + 按 API 费率的用量”），按使用强度与工作画像逐用户估算。",
      "API 估算为粗略行业数字——编码/智能体工作（Claude Code）消耗的 token 远多于聊天类工作——以 Claude 的 API 费率为基础，且可编辑。",
      "Team 月付席位比年付贵（每席位 $25/$125 对 $20/$100）；Enterprise 为年付 + 按量计费。令吉按约 1 美元兑 4.09 令吉（2026 年 8 月 11 日）换算。席位价格以 claude.com/pricing 为准（请核对最新数字）。",
    ],
    disclaimer:
      "仅为规划估算，并非 Anthropic 官方报价。实际价格取决于席位数、用量与谈判。",
    ctaTitle: "想要精确的 Enterprise 报价与落地方案？",
    ctaDesc:
      "我们会帮你选定合适方案、拿到真实的 Enterprise 报价，并规划部署——含 SSO、治理与赋能。",
    ctaBtn: "联系我们的团队",
    buyTitle: "无法用信用卡向 Anthropic 付款？",
    buyDesc:
      "我们协助企业以采购订单购买 Claude Team 或 Enterprise，并开具令吉或新元发票。用信用卡付款？直接向 Anthropic 购买即可。",
    buyBtn: "WhatsApp：我想购买 Claude",
    pricingBlog: "阅读完整的马来西亚 Claude 价格指南",
    managedService: "或了解由谁部署 Claude 并持续运维",
    agentCost: "这是授权与 API 的成本——再看看把智能体建起来要多少",
    mcpLink: "Claude 也可以连接你现有的系统（MCP）",
    faqTitle: "团队常问的问题",
    faqs: [
      {
        q: "在马来西亚，Claude 每位用户的成本是多少？",
        a: "在 Team 方案下，一个 Claude 席位约为每位用户每月 RM95（年付 US$20；按月约 RM102），更高用量的 Premium 席位约每月 RM470（US$100）——用量已含在席位中。Enterprise 则是较低的统一席位价（约 US$20）另加按 API 计费的用量，因此每位用户的成本随工作类型而变：轻度业务运营用户通常约 RM110/月，重度编码/工程用户按 API 费率可达 RM1,500–3,800/月。在上方设定你的用户分布，即可查看以 RM 计的每位用户与三年成本。",
      },
      {
        q: "Claude Team 与 Enterprise 有何区别？",
        a: "Team 是面向较小团队的自助按席位授权（从 5 席位起），含 Standard 与更高用量的 Premium 席位。Enterprise 增加 SSO/SCIM、进阶安全与合规控制、更高用量上限、更大上下文与管理治理——采用定制协商定价。",
      },
      {
        q: "这是 Anthropic 官方价格吗？",
        a: "不是。Team 席位价按当前公布费率预填，但每个字段均可编辑，Enterprise 数字为可编辑的规划估算。Enterprise 始终为定制报价——联系我们获取真实报价。",
      },
      {
        q: "三年 TCO 如何计算？",
        a: "Team 方案下，轻度/常规用户按 Standard 席位、重度用户按 Premium 席位计价（用量已含）。Enterprise 则是较低的统一席位价，另加按 API 计费的用量，按使用强度与工作画像（业务运营 vs 编码）逐用户估算。两者均乘以 36 个月，并可选一次性落地费用。",
      },
      {
        q: "为什么使用分布很重要？",
        a: "全天重度用户可能超出 Standard 席位的用量而需要 Premium 席位。重度用户越多，Team 上购买的 Premium 席位越多——这正是统一的 Enterprise 席位往往更便宜之处。",
      },
      {
        q: "Anchor Sprint 能帮我们部署 Claude 吗？",
        a: "可以。我们帮你选定合适方案、争取 Enterprise 报价、配置 SSO 与治理，并开展提示培训与变革管理，让采用真正落地。",
      },
    ],
  },
};

function NumberField({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  prefix,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-neutral-muted">
        {label}
      </span>
      <span className="flex items-center rounded-lg border border-border bg-background px-3 py-2 focus-within:border-primary">
        {prefix ? (
          <span className="mr-1 text-sm text-neutral-muted">{prefix}</span>
        ) : null}
        <input
          type="number"
          inputMode="decimal"
          value={Number.isFinite(value) ? value : 0}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full bg-transparent text-sm font-semibold text-neutral-emphasis outline-none"
        />
        {suffix ? (
          <span className="ml-1 shrink-0 text-xs text-neutral-muted">
            {suffix}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export default function ClaudeCostCalculator({
  locale = defaultLocale,
}: {
  locale?: Locale;
}) {
  const t = UI[locale] ?? UI[defaultLocale];

  const [users, setUsers] = useState(50);
  const [lowPct, setLowPct] = useState(40);
  const [avgPct, setAvgPct] = useState(40);
  const heavyPct = Math.max(0, 100 - lowPct - avgPct);
  const [standard, setStandard] = useState(DEFAULTS.standard);
  const [premium, setPremium] = useState(DEFAULTS.premium);
  const [enterpriseSeat, setEnterpriseSeat] = useState(DEFAULTS.enterpriseSeat);
  const [profile, setProfile] = useState<"ops" | "coding">("ops");
  const [apiLight, setApiLight] = useState(API_DEFAULTS.ops.light);
  const [apiRegular, setApiRegular] = useState(API_DEFAULTS.ops.regular);
  const [apiHeavy, setApiHeavy] = useState(API_DEFAULTS.ops.heavy);
  const [monthly, setMonthly] = useState(false);
  const [currency, setCurrency] = useState<CurrencyCode>(DEFAULT_CURRENCY);

  // Auto-select the currency from the visitor's browser region on mount. Runs
  // client-side only (navigator/Intl), so the static HTML renders the default
  // first and this refines it after hydration — no mismatch.
  useEffect(() => {
    const detected = detectCurrency();
    if (detected) setCurrency(detected);
  }, []);
  const [withServices, setWithServices] = useState(false);
  const [servicesRM, setServicesRM] = useState(DEFAULTS.servicesRM);
  const [busy, setBusy] = useState(false);
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [gate, setGate] = useState<"idle" | "loading" | "done" | "error">(
    "idle",
  );

  const setAvgSafe = (v: number) => setAvgPct(Math.min(v, 100 - lowPct));
  const setLowSafe = (v: number) => {
    setLowPct(v);
    if (v + avgPct > 100) setAvgPct(100 - v);
  };
  const selectProfile = (p: "ops" | "coding") => {
    setProfile(p);
    setApiLight(API_DEFAULTS[p].light);
    setApiRegular(API_DEFAULTS[p].regular);
    setApiHeavy(API_DEFAULTS[p].heavy);
  };
  // Switch Team seat prices to the real annual vs monthly rates (Enterprise stays annual).
  const selectBilling = (m: boolean) => {
    setMonthly(m);
    const p = m ? TEAM_PRICES.monthly : TEAM_PRICES.annual;
    setStandard(p.standard);
    setPremium(p.premium);
  };

  const calc = useMemo(() => {
    const seatsLow = Math.round((users * lowPct) / 100);
    const seatsAvg = Math.round((users * avgPct) / 100);
    const seatsHeavy = Math.max(0, users - seatsLow - seatsAvg);
    // Team: flat per-seat, usage bundled (light/regular → Standard, heavy → Premium).
    // Seat prices already reflect the chosen billing cycle (annual vs monthly).
    const teamMo = (seatsLow + seatsAvg) * standard + seatsHeavy * premium;
    // Enterprise: low (annual, negotiated) seat + metered API usage at API rates.
    const entSeatMo = users * enterpriseSeat;
    const entApiMo =
      seatsLow * apiLight + seatsAvg * apiRegular + seatsHeavy * apiHeavy;
    const entMo = entSeatMo + entApiMo;
    const servicesUSD = withServices ? servicesRM / RM_PER_USD : 0;
    const team3 = teamMo * 36 + servicesUSD;
    const ent3 = entMo * 36 + servicesUSD;
    return {
      seatsLow,
      seatsAvg,
      seatsHeavy,
      teamMo,
      entMo,
      entSeatMo,
      entApiMo,
      teamYr: teamMo * 12,
      entYr: entMo * 12,
      team3,
      ent3,
      servicesUSD,
      teamPerUser: users ? teamMo / users : 0,
      entPerUser: users ? entMo / users : 0,
      diff: Math.abs(team3 - ent3),
      teamWins: team3 <= ent3,
    };
  }, [
    users,
    lowPct,
    avgPct,
    standard,
    premium,
    enterpriseSeat,
    apiLight,
    apiRegular,
    apiHeavy,
    withServices,
    servicesRM,
  ]);

  const cinfo = CURRENCIES[currency];
  const nf = useMemo(
    () => new Intl.NumberFormat(cinfo.locale, { maximumFractionDigits: 0 }),
    [cinfo.locale],
  );
  // Prefix our own symbol (space for multi-char symbols like "RM"/"S$"/"Rp")
  // so it always matches the dropdown and never collides with USD's "$".
  const fmt = (usd: number) =>
    `${cinfo.symbol}${cinfo.symbol.length > 1 ? " " : ""}${nf.format(
      Math.round(usd * cinfo.perUsd),
    )}`;

  // Cumulative-cost chart geometry (custom SVG, live-updating).
  const chart = useMemo(() => {
    const W = 600,
      H = 240,
      padL = 12,
      padR = 12,
      padT = 20,
      padB = 26;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const maxY = Math.max(calc.team3, calc.ent3, 1);
    const x = (m: number) => padL + (m / 36) * plotW;
    const y = (v: number) => padT + (1 - v / maxY) * plotH;
    const teamV = (m: number) => calc.servicesUSD + calc.teamMo * m;
    const entV = (m: number) => calc.servicesUSD + calc.entMo * m;
    const pt = (m: number, v: number) => `${x(m)},${y(v)}`;
    return {
      W,
      H,
      padT,
      padB,
      plotH,
      x,
      y,
      teamLine: `${pt(0, teamV(0))} ${pt(36, teamV(36))}`,
      entLine: `${pt(0, entV(0))} ${pt(36, entV(36))}`,
      // polygon between the two lines = the saving
      gap: `${pt(0, teamV(0))} ${pt(36, teamV(36))} ${pt(36, entV(36))} ${pt(0, entV(0))}`,
      teamEnd: [x(36), y(teamV(36))] as [number, number],
      entEnd: [x(36), y(entV(36))] as [number, number],
      gapMid: [x(20), (y(teamV(20)) + y(entV(20))) / 2] as [number, number],
      months: [12, 24, 36],
    };
  }, [calc]);

  // Draw the cumulative-cost chart to a PNG (base64) for embedding in Excel.
  function renderChartPng(): string {
    const W = 660,
      H = 300;
    const pad = { l: 20, r: 120, t: 34, b: 34 };
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
    const plotW = W - pad.l - pad.r;
    const plotH = H - pad.t - pad.b;
    const maxY = Math.max(calc.team3, calc.ent3, 1);
    const x = (m: number) => pad.l + (m / 36) * plotW;
    const y = (v: number) => pad.t + (1 - v / maxY) * plotH;
    const teamV = (m: number) => calc.servicesUSD + calc.teamMo * m;
    const entV = (m: number) => calc.servicesUSD + calc.entMo * m;
    ctx.fillStyle = "#0f172a";
    ctx.font = "bold 15px Arial";
    ctx.fillText("Cumulative cost over 3 years", pad.l, 20);
    ctx.strokeStyle = "#e2e8f0";
    ctx.beginPath();
    ctx.moveTo(pad.l, y(0));
    ctx.lineTo(x(36), y(0));
    ctx.stroke();
    ctx.fillStyle = "#94a3b8";
    ctx.font = "12px Arial";
    ctx.textAlign = "center";
    [12, 24, 36].forEach((m, i) => {
      ctx.strokeStyle = "#f1f5f9";
      ctx.beginPath();
      ctx.moveTo(x(m), pad.t);
      ctx.lineTo(x(m), y(0));
      ctx.stroke();
      ctx.fillText(`Y${i + 1}`, x(m), y(0) + 18);
    });
    // savings gap
    ctx.fillStyle = "rgba(37,99,235,0.12)";
    ctx.beginPath();
    ctx.moveTo(x(0), y(teamV(0)));
    ctx.lineTo(x(36), y(teamV(36)));
    ctx.lineTo(x(36), y(entV(36)));
    ctx.lineTo(x(0), y(entV(0)));
    ctx.closePath();
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#94a3b8";
    ctx.beginPath();
    ctx.moveTo(x(0), y(entV(0)));
    ctx.lineTo(x(36), y(entV(36)));
    ctx.stroke();
    ctx.strokeStyle = "#2563eb";
    ctx.beginPath();
    ctx.moveTo(x(0), y(teamV(0)));
    ctx.lineTo(x(36), y(teamV(36)));
    ctx.stroke();
    ctx.textAlign = "left";
    ctx.font = "bold 12px Arial";
    ctx.fillStyle = "#2563eb";
    ctx.fillText(`Team ${fmt(calc.team3)}`, x(36) + 6, y(teamV(36)) + 4);
    ctx.fillStyle = "#64748b";
    ctx.fillText(`Ent ${fmt(calc.ent3)}`, x(36) + 6, y(entV(36)) + 4);
    return canvas.toDataURL("image/png").split(",")[1];
  }

  async function downloadExcel(company: string, email: string) {
    setBusy(true);
    try {
      const ExcelJSmod: any = await import("exceljs");
      const ExcelJS = ExcelJSmod.default ?? ExcelJSmod;
      const cur = currency;
      const money = (usd: number) => Math.round(usd * CURRENCIES[currency].perUsd);
      const numFmt = CURRENCIES[currency].excelFmt;
      const BLUE = "FF2563EB";
      const NAVY = "FF0F172A";
      const LIGHT = "FFF1F5F9";
      const GREEN = "FF16A34A";
      const MUTED = "FF64748B";
      const HILITE = "FFEFF6FF";
      const WHITE = "FFFFFFFF";
      const fill = (argb: string) =>
        ({ type: "pattern", pattern: "solid", fgColor: { argb } }) as const;

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Claude TCO", {
        views: [{ showGridLines: false }],
      });
      ws.columns = [{ width: 40 }, { width: 20 }, { width: 20 }];

      const titleRow = ws.addRow(["Claude Team vs Enterprise — 3-Year TCO"]);
      ws.mergeCells(`A${titleRow.number}:C${titleRow.number}`);
      titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: WHITE } };
      titleRow.getCell(1).fill = fill(BLUE);
      titleRow.getCell(1).alignment = { vertical: "middle" };
      titleRow.height = 28;
      const pf = ws.addRow([`${t.preparedFor}: ${company}`]);
      pf.getCell(1).font = { bold: true, color: { argb: NAVY } };
      if (email) ws.addRow([`Contact: ${email}`]);
      const urlRow = ws.addRow([
        "anchorsprint.com/tools/claude-cost-calculator",
      ]);
      urlRow.getCell(1).font = { italic: true, size: 10, color: { argb: MUTED } };
      ws.addRow([]);

      const sectionHeader = (label: string, argb = LIGHT) => {
        const r = ws.addRow([label]);
        r.getCell(1).font = { bold: true, color: { argb: NAVY } };
        r.getCell(1).fill = fill(argb);
        return r;
      };
      sectionHeader("Inputs");
      (
        [
          ["Total users", users],
          ["Usage mix — light / regular / heavy (%)", `${lowPct} / ${avgPct} / ${heavyPct}`],
          ["Work profile", profile === "coding" ? "Coding / tech" : "Business ops"],
          ["Billing", monthly ? "Monthly" : "Annual"],
          ["Team Standard (US$/seat/mo)", standard],
          ["Team Premium (US$/seat/mo)", premium],
          ["Enterprise seat (US$/seat/mo)", enterpriseSeat],
          ["Enterprise API US$/user — light / reg / heavy", `${apiLight} / ${apiRegular} / ${apiHeavy}`],
          ["One-time onboarding (RM)", withServices ? servicesRM : 0],
        ] as [string, string | number][]
      ).forEach((row) => ws.addRow(row));
      ws.addRow([]);

      const tableHeader = (cols: string[], bg: string) => {
        const r = ws.addRow(cols);
        r.eachCell((c: any) => {
          c.font = { bold: true, color: { argb: WHITE } };
          c.fill = fill(bg);
          c.alignment = { horizontal: "left" };
        });
        return r;
      };
      const winCol = calc.teamWins ? 2 : 3;
      tableHeader([`3-year comparison (${cur})`, "Team plan", "Enterprise plan"], BLUE);
      (
        [
          ["Per month", money(calc.teamMo), money(calc.entMo)],
          ["Per year", money(calc.teamYr), money(calc.entYr)],
          ["3-year TCO", money(calc.team3), money(calc.ent3)],
          ["Per user / month", money(calc.teamPerUser), money(calc.entPerUser)],
        ] as [string, number, number][]
      ).forEach((row, idx) => {
        const rr = ws.addRow(row);
        rr.getCell(2).numFmt = numFmt;
        rr.getCell(3).numFmt = numFmt;
        rr.getCell(winCol).fill = fill(HILITE);
        if (idx === 2)
          rr.eachCell((c: any) => (c.font = { bold: true }));
      });
      const es = ws.addRow([
        "Enterprise split — seat + API usage",
        money(calc.entSeatMo),
        money(calc.entApiMo),
      ]);
      es.getCell(2).numFmt = numFmt;
      es.getCell(3).numFmt = numFmt;
      es.getCell(1).font = { italic: true, size: 10, color: { argb: MUTED } };
      ws.addRow([]);

      tableHeader(["Year-by-year", "Team plan", "Enterprise plan"], NAVY);
      (
        [
          [
            "Year 1 (incl. onboarding)",
            money(calc.teamYr + calc.servicesUSD),
            money(calc.entYr + calc.servicesUSD),
          ],
          ["Year 2", money(calc.teamYr), money(calc.entYr)],
          ["Year 3", money(calc.teamYr), money(calc.entYr)],
          ["3-year total", money(calc.team3), money(calc.ent3)],
        ] as [string, number, number][]
      ).forEach((row, i) => {
        const rr = ws.addRow(row);
        rr.getCell(2).numFmt = numFmt;
        rr.getCell(3).numFmt = numFmt;
        if (i === 3) rr.eachCell((c: any) => (c.font = { bold: true }));
      });
      ws.addRow([]);

      const rec = ws.addRow([
        calc.teamWins
          ? `Team is about ${fmt(calc.diff)} cheaper over 3 years`
          : `Enterprise is about ${fmt(calc.diff)} cheaper over 3 years`,
      ]);
      ws.mergeCells(`A${rec.number}:C${rec.number}`);
      rec.getCell(1).font = { bold: true, color: { argb: WHITE } };
      rec.getCell(1).fill = fill(GREEN);
      rec.height = 22;
      ws.addRow([]);

      // Embedded chart image
      const png = renderChartPng();
      if (png) {
        const imgId = wb.addImage({ base64: png, extension: "png" });
        const top = ws.lastRow.number; // 0-indexed row for placement
        ws.addImage(imgId, {
          tl: { col: 0, row: top },
          ext: { width: 480, height: 218 },
        });
        for (let i = 0; i < 12; i++) ws.addRow([]);
      }

      const note = ws.addRow([
        "Indicative planning estimate only, not an official Anthropic quote. Enterprise API usage is an editable estimate; confirm current Anthropic pricing before committing.",
      ]);
      ws.mergeCells(`A${note.number}:C${note.number}`);
      note.getCell(1).font = { italic: true, size: 9, color: { argb: MUTED } };
      note.getCell(1).alignment = { wrapText: true };
      note.height = 30;

      const slug =
        company
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
          .slice(0, 40) || "claude";
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `claude-tco-${slug}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
    } finally {
      setBusy(false);
    }
  }

  async function handleLeadAndDownload(e: React.FormEvent) {
    e.preventDefault();
    const key = process.env.NEXT_PUBLIC_WEB3FORMS_KEY;
    // If lead capture isn't configured, do NOT silently release the file — a
    // download with no captured lead is exactly the failure we're fixing.
    if (!key || key === "your_actual_web3forms_key_here") {
      setGate("error");
      return;
    }
    setGate("loading");
    const money = (usd: number) => Math.round(usd * CURRENCIES[currency].perUsd);
    const summary =
      `Claude TCO lead — ${company}. ${users} users ` +
      `(${lowPct}% light / ${avgPct}% regular / ${heavyPct}% heavy). ` +
      `Team ${currency} ${money(calc.team3)} vs Enterprise ${currency} ${money(
        calc.ent3,
      )} over 3 years; ${
        calc.teamWins ? "Team" : "Enterprise"
      } cheaper by ${currency} ${money(calc.diff)}. ` +
      `Profile: ${profile === "coding" ? "coding/tech" : "business ops"}. ` +
      `Seats: Std $${standard} / Prem $${premium} / Ent seat $${enterpriseSeat}. ` +
      `Ent API $/user: ${apiLight}/${apiRegular}/${apiHeavy} (light/reg/heavy); ` +
      `${monthly ? "monthly" : "annual"} billing.`;
    try {
      const body = new FormData();
      body.append("access_key", key);
      body.append("name", company);
      body.append("company", company);
      body.append("email", email);
      body.append("request_type", "Claude TCO Calculator");
      body.append("source", "claude-cost-calculator");
      body.append("subject", `Claude TCO lead — ${company}`);
      body.append("from_name", "Anchor Sprint Website");
      body.append("message", withMarketingConsentLine(summary, marketingConsent));
      appendMarketingConsent(body, marketingConsent, locale);
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body,
      });
      const data = await res.json();
      // Only count the lead + release the file once Web3Forms confirms receipt.
      if (!data.success) {
        throw new Error(data.message || "Web3Forms submission failed");
      }
      trackEvent("claude_tco_lead", {
        category: "lead",
        label: "claude_cost_calculator",
        source: "claude-cost-calculator",
        email_provided: true,
      });
      trackDownload("claude-tco.xlsx", "xlsx", {
        gated: true,
        source: "claude-cost-calculator",
      });
      await downloadExcel(company, email);
      setGate("done");
    } catch {
      // Capture failed — surface an error with a WhatsApp fallback instead of
      // logging a phantom lead or handing over the file with no email sent.
      setGate("error");
    }
  }

  const faqForSchema = t.faqs.map((f) => ({ question: f.q, answer: f.a }));

  return (
    <div className="bg-background">
      <StructuredData type="faq" faqs={faqForSchema} />

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 pt-24 pb-8 sm:px-6 lg:px-8 lg:pt-32">
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.25em] text-primary">
          <Sparkles className="h-4 w-4" aria-hidden />
          {t.eyebrow}
        </p>
        <h1 className="max-w-3xl font-display text-4xl font-bold leading-[1.1] tracking-tight text-neutral-emphasis md:text-5xl">
          {t.title}
        </h1>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-muted">
          {t.intro}
        </p>
        <p className="mt-3 text-sm font-medium text-primary">
          {t.credibility} {t.partnerLead}
          <Link
            href={getLocalizedPathname("/partners/anthropic-claude-partner/", locale)}
            className="underline underline-offset-4"
          >
            {t.partnerLink}
          </Link>
          {t.partnerTail}
        </p>
      </section>

      {/* Calculator */}
      <section className="mx-auto max-w-6xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-5">
          {/* Inputs */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="mb-5 font-display text-lg font-semibold text-neutral-emphasis">
                {t.inputsTitle}
              </h2>

              {/* Users */}
              <div className="mb-6">
                <div className="mb-2 flex items-baseline justify-between">
                  <span className="text-sm font-medium text-neutral-emphasis">
                    {t.totalUsers}
                  </span>
                  <span className="font-display text-2xl font-bold text-primary">
                    {users}
                  </span>
                </div>
                <Slider
                  value={[users]}
                  min={5}
                  max={1000}
                  step={5}
                  onValueChange={([v]) => setUsers(v)}
                  aria-label={t.totalUsers}
                />
              </div>

              {/* Usage mix */}
              <div className="mb-6">
                <span className="text-sm font-medium text-neutral-emphasis">
                  {t.usageMix}
                </span>
                <p className="mb-3 mt-1 text-xs leading-relaxed text-neutral-muted">
                  {t.usageHelp}
                </p>
                {/* stacked bar */}
                <div className="mb-4 flex h-3 overflow-hidden rounded-full">
                  <span
                    className="bg-info"
                    style={{ width: `${lowPct}%` }}
                    aria-hidden
                  />
                  <span
                    className="bg-primary"
                    style={{ width: `${avgPct}%` }}
                    aria-hidden
                  />
                  <span
                    className="bg-warning"
                    style={{ width: `${heavyPct}%` }}
                    aria-hidden
                  />
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-medium text-neutral-emphasis">
                        {t.low}{" "}
                        <span className="text-neutral-muted">
                          ({calc.seatsLow})
                        </span>
                      </span>
                      <span className="font-semibold text-info">{lowPct}%</span>
                    </div>
                    <Slider
                      value={[lowPct]}
                      min={0}
                      max={100}
                      step={5}
                      onValueChange={([v]) => setLowSafe(v)}
                      aria-label={t.low}
                    />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-medium text-neutral-emphasis">
                        {t.average}{" "}
                        <span className="text-neutral-muted">
                          ({calc.seatsAvg})
                        </span>
                      </span>
                      <span className="font-semibold text-primary">
                        {avgPct}%
                      </span>
                    </div>
                    <Slider
                      value={[avgPct]}
                      min={0}
                      max={100}
                      step={5}
                      onValueChange={([v]) => setAvgSafe(v)}
                      aria-label={t.average}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-warning/10 px-3 py-2 text-xs">
                    <span className="font-medium text-neutral-emphasis">
                      {t.heavy}{" "}
                      <span className="text-neutral-muted">
                        ({calc.seatsHeavy})
                      </span>
                    </span>
                    <span className="font-semibold text-warning">
                      {heavyPct}%
                    </span>
                  </div>
                  <p className="text-[11px] leading-relaxed text-neutral-muted">
                    {t.heavyHelp}
                  </p>
                </div>
              </div>

              {/* Pricing */}
              <div className="mb-6">
                <span className="text-sm font-medium text-neutral-emphasis">
                  {t.pricingTitle}
                </span>
                <p className="mb-3 mt-1 text-xs leading-relaxed text-neutral-muted">
                  {t.pricingHelp}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <NumberField
                    label={t.standard}
                    value={standard}
                    onChange={setStandard}
                    prefix="$"
                  />
                  <NumberField
                    label={t.premium}
                    value={premium}
                    onChange={setPremium}
                    prefix="$"
                  />
                  <NumberField
                    label={t.enterprise}
                    value={enterpriseSeat}
                    onChange={setEnterpriseSeat}
                    prefix="$"
                  />
                </div>
                <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-neutral-muted">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                  {t.enterpriseNote}
                </p>
              </div>

              {/* Enterprise API usage (metered) */}
              <div className="mb-6 rounded-xl border border-warning/30 bg-warning/5 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-neutral-emphasis">
                    {t.apiTitle}
                  </span>
                  <div className="flex rounded-lg border border-border p-0.5">
                    {(
                      [
                        { k: "ops", label: t.opsLabel },
                        { k: "coding", label: t.codingLabel },
                      ] as const
                    ).map((o) => (
                      <button
                        key={o.k}
                        type="button"
                        onClick={() => selectProfile(o.k)}
                        className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                          profile === o.k
                            ? "bg-primary text-primary-foreground"
                            : "text-neutral-muted hover:text-neutral-emphasis"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="mb-3 mt-1 text-xs leading-relaxed text-neutral-muted">
                  {t.profileHelp}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <NumberField
                    label={t.low}
                    value={apiLight}
                    onChange={setApiLight}
                    prefix="$"
                  />
                  <NumberField
                    label={t.average}
                    value={apiRegular}
                    onChange={setApiRegular}
                    prefix="$"
                  />
                  <NumberField
                    label={t.heavy}
                    value={apiHeavy}
                    onChange={setApiHeavy}
                    prefix="$"
                  />
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-neutral-muted">
                  {t.apiHelp}
                </p>
              </div>

              {/* Billing */}
              <div className="mb-6">
                <span className="mb-1 block text-xs font-medium text-neutral-muted">
                  {t.billing}
                </span>
                <div className="flex rounded-lg border border-border p-0.5">
                  {[
                    { k: false, label: t.annual },
                    { k: true, label: t.monthly },
                  ].map((o) => (
                    <button
                      key={String(o.k)}
                      type="button"
                      onClick={() => selectBilling(o.k)}
                      className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
                        monthly === o.k
                          ? "bg-primary text-primary-foreground"
                          : "text-neutral-muted hover:text-neutral-emphasis"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="mb-6 text-[11px] leading-relaxed text-neutral-muted">
                {t.annualNote}
              </p>

              {/* Services */}
              <div className="rounded-xl border border-border bg-background p-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={withServices}
                    onChange={(e) => setWithServices(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-primary"
                  />
                  <span>
                    <span className="block text-sm font-medium text-neutral-emphasis">
                      {t.services}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-neutral-muted">
                      {t.servicesHelp}
                    </span>
                  </span>
                </label>
                {withServices ? (
                  <div className="mt-3">
                    <NumberField
                      label={t.oneTimeRm}
                      value={servicesRM}
                      onChange={setServicesRM}
                      prefix="RM"
                      step={1000}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl bg-neutral-emphasis p-6 text-background sm:p-8">
              <div className="mb-6 flex items-center justify-between gap-4">
                <h2 className="font-display text-lg font-semibold">
                  {t.resultTitle}
                </h2>
                <label className="flex shrink-0 items-center gap-2 text-xs font-medium text-background/70">
                  <span className="sr-only sm:not-sr-only">{t.currency}</span>
                  <select
                    value={currency}
                    onChange={(e) =>
                      setCurrency(e.target.value as CurrencyCode)
                    }
                    aria-label={t.currency}
                    className="rounded-lg border border-background/25 bg-transparent px-2.5 py-1.5 text-xs font-semibold text-background focus:outline-none focus:ring-1 focus:ring-background/40 [&>option]:text-neutral-emphasis"
                  >
                    {CURRENCY_CODES.map((c) => (
                      <option key={c} value={c}>
                        {CURRENCIES[c].label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  {
                    name: t.teamPlan,
                    mo: calc.teamMo,
                    yr: calc.teamYr,
                    three: calc.team3,
                    per: calc.teamPerUser,
                    win: calc.teamWins,
                    split: null as { seat: number; api: number } | null,
                  },
                  {
                    name: t.enterprisePlan,
                    mo: calc.entMo,
                    yr: calc.entYr,
                    three: calc.ent3,
                    per: calc.entPerUser,
                    win: !calc.teamWins,
                    split: { seat: calc.entSeatMo, api: calc.entApiMo },
                  },
                ].map((p) => (
                  <div
                    key={p.name}
                    className={`rounded-xl border p-5 ${
                      p.win
                        ? "border-primary bg-primary/15"
                        : "border-white/15 bg-white/5"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">{p.name}</span>
                      {p.win ? (
                        <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">
                          {t.recommendation}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-3 font-display text-3xl font-bold">
                      {fmt(p.three)}
                    </p>
                    <p className="text-xs text-background/60">{t.threeYr}</p>
                    <dl className="mt-4 space-y-1 border-t border-white/10 pt-3 text-xs">
                      <div className="flex justify-between">
                        <dt className="text-background/60">{t.perMonth}</dt>
                        <dd className="font-semibold">{fmt(p.mo)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-background/60">{t.perYear}</dt>
                        <dd className="font-semibold">{fmt(p.yr)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-background/60">{t.perUserMo}</dt>
                        <dd className="font-semibold">{fmt(p.per)}</dd>
                      </div>
                      {p.split ? (
                        <div className="mt-1 flex justify-between border-t border-white/10 pt-1 text-[11px] text-background/50">
                          <dt>
                            {t.seatWord} {fmt(p.split.seat)}
                          </dt>
                          <dd>
                            + {t.apiWord} {fmt(p.split.api)}
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                ))}
              </div>

              {/* Buy signal at the moment the reader has a plan and a price (Jazz, 2026-10-28).
                  The message carries plan, seats and invoice currency so sales can quote at once.
                  Purchase help only: we are not a reseller, and card buyers go direct. */}
              <div className="mt-4 flex flex-col gap-3 rounded-xl border border-primary/40 bg-primary/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold">{t.buyTitle}</p>
                  <p className="mt-1 text-xs leading-relaxed text-background/70">{t.buyDesc}</p>
                </div>
                <a
                  href={whatsappHref(
                    `Hi Anchor Sprint, I want to buy Claude ${calc.teamWins ? "Team" : "Enterprise"} for ${users} users, paid by purchase order with an ${currency === "SGD" ? "SGD" : "RM"} invoice (from the Claude cost calculator).`,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track-event="contact_whatsapp_click"
                  data-track-location="claude_cost_calculator_buy"
                  data-track-phone={WHATSAPP}
                  className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
                >
                  <FaWhatsapp className="h-4 w-4" aria-hidden />
                  {t.buyBtn}
                </a>
              </div>

              {/* Cumulative-cost financial chart */}
              <div className="mt-6">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-background/60">
                    {t.chartTitle}
                  </span>
                  <span className="flex items-center gap-3 text-[11px]">
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-3 rounded-sm bg-primary" aria-hidden />
                      {t.teamPlan}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-3 rounded-sm bg-white/50" aria-hidden />
                      {t.enterprisePlan}
                    </span>
                  </span>
                </div>
                <svg
                  viewBox={`0 0 ${chart.W} ${chart.H}`}
                  className="w-full"
                  role="img"
                  aria-label={`${t.chartTitle}: ${t.teamPlan} ${fmt(
                    calc.team3,
                  )}, ${t.enterprisePlan} ${fmt(calc.ent3)}`}
                >
                  {/* baseline */}
                  <line
                    x1="12"
                    y1={chart.padT + chart.plotH}
                    x2={chart.W - 12}
                    y2={chart.padT + chart.plotH}
                    stroke="rgba(255,255,255,0.15)"
                    strokeWidth="1"
                  />
                  {/* year gridlines + labels */}
                  {chart.months.map((m, i) => (
                    <g key={m}>
                      <line
                        x1={chart.x(m)}
                        y1={chart.padT}
                        x2={chart.x(m)}
                        y2={chart.padT + chart.plotH}
                        stroke="rgba(255,255,255,0.08)"
                        strokeWidth="1"
                      />
                      <text
                        x={chart.x(m)}
                        y={chart.padT + chart.plotH + 18}
                        textAnchor="middle"
                        className="fill-background/50"
                        fontSize="12"
                      >
                        {`Y${i + 1}`}
                      </text>
                    </g>
                  ))}
                  {/* savings gap */}
                  <polygon points={chart.gap} fill="rgba(37,99,235,0.16)" />
                  {/* enterprise + team lines */}
                  <polyline
                    points={chart.entLine}
                    fill="none"
                    stroke="rgba(255,255,255,0.5)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <polyline
                    points={chart.teamLine}
                    fill="none"
                    stroke="rgb(37,99,235)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  {/* endpoint dots */}
                  <circle cx={chart.entEnd[0]} cy={chart.entEnd[1]} r="3.5" fill="rgba(255,255,255,0.7)" />
                  <circle cx={chart.teamEnd[0]} cy={chart.teamEnd[1]} r="3.5" fill="rgb(37,99,235)" />
                  {/* savings annotation */}
                  <text
                    x={chart.gapMid[0]}
                    y={chart.gapMid[1]}
                    textAnchor="middle"
                    className="fill-primary"
                    fontSize="12"
                    fontWeight="700"
                  >
                    {`${t.savingsLabel} ${fmt(calc.diff)}`}
                  </text>
                </svg>
              </div>

              {/* recommendation */}
              <div className="mt-6 rounded-xl bg-white/5 p-4">
                <p className="text-sm font-semibold text-primary">
                  {calc.teamWins
                    ? t.teamCheaper(fmt(calc.diff))
                    : t.entCheaper(fmt(calc.diff))}
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-background/70">
                  {calc.teamWins ? t.teamWhy : t.entWhy}
                </p>
                <p className="mt-2 text-[11px] leading-relaxed text-background/50">
                  {t.minSeats}
                </p>
              </div>

              {/* Lead-gated, company-branded Excel download */}
              <div className="mt-6 rounded-xl border border-white/15 bg-white/5 p-4">
                {gate === "done" ? (
                  <div className="text-center">
                    <CheckCircle2
                      className="mx-auto h-8 w-8 text-primary"
                      aria-hidden
                    />
                    <p className="mt-2 text-sm font-semibold">{t.gateSuccess}</p>
                    <p className="mt-1 text-xs text-background/60">
                      {t.gateSuccessDesc}{" "}
                      <button
                        type="button"
                        onClick={() => downloadExcel(company, email)}
                        className="font-semibold text-primary underline underline-offset-2"
                      >
                        {t.downloadAgain}
                      </button>
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleLeadAndDownload}>
                    <p className="flex items-center gap-2 text-sm font-semibold">
                      <Download className="h-4 w-4 text-primary" aria-hidden />
                      {t.gateTitle}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-background/60">
                      {t.gateDesc}
                    </p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <input
                        type="text"
                        required
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder={t.companyLabel}
                        aria-label={t.companyLabel}
                        className="rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-sm text-background placeholder:text-background/40 focus:border-primary focus:outline-none"
                      />
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t.emailLabel}
                        aria-label={t.emailLabel}
                        className="rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-sm text-background placeholder:text-background/40 focus:border-primary focus:outline-none"
                      />
                    </div>
                    <MarketingConsent
                      id="tco-marketing-consent"
                      checked={marketingConsent}
                      onChange={setMarketingConsent}
                      locale={locale}
                      label={t.consentLabel}
                      privacyLabel={t.privacyNotice}
                      labelClassName="text-background/70"
                      className="mt-3"
                    />
                    <button
                      type="submit"
                      disabled={gate === "loading" || busy}
                      data-track-event="tco_excel_download"
                      data-track-location="claude_cost_calculator"
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis disabled:opacity-60"
                    >
                      {gate === "loading" || busy ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          {t.generating}
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" aria-hidden />
                          {t.generate}
                        </>
                      )}
                    </button>
                    {gate === "error" ? (
                      <div className="mt-3 rounded-lg border-l-2 border-destructive bg-white/10 px-3 py-2 text-xs leading-relaxed text-background">
                        {t.gateError}{" "}
                        <a
                          href={whatsappHref(
                            "Hi Anchor Sprint, I'd like help estimating Claude costs for my team (from the Claude cost calculator).",
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-track-event="contact_whatsapp_click"
                          data-track-location="claude_cost_calculator_gate_error"
                          data-track-phone={WHATSAPP}
                          className="inline-flex items-center gap-1 font-semibold text-primary underline underline-offset-2"
                        >
                          <FaWhatsapp className="h-3.5 w-3.5" aria-hidden />
                          WhatsApp
                        </a>
                      </div>
                    ) : null}
                    <p className="mt-2 text-[11px] leading-relaxed text-background/50">
                      {t.privacy}
                    </p>
                  </form>
                )}
              </div>
              <p className="mt-3 text-center text-[11px] leading-relaxed text-background/50">
                {t.disclaimer}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Breakdown table */}
      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-semibold text-neutral-emphasis">
            {t.breakdownTitle}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-neutral-muted">
                  <th className="py-2 pr-4 font-semibold">{t.year}</th>
                  <th className="py-2 pr-4 font-semibold">{t.teamPlan}</th>
                  <th className="py-2 font-semibold">{t.enterprisePlan}</th>
                </tr>
              </thead>
              <tbody className="text-neutral-emphasis">
                {[
                  {
                    y: `${t.year} 1`,
                    tv: calc.teamYr + calc.servicesUSD,
                    ev: calc.entYr + calc.servicesUSD,
                    note: withServices ? `+ ${t.onboarding}` : "",
                  },
                  { y: `${t.year} 2`, tv: calc.teamYr, ev: calc.entYr },
                  { y: `${t.year} 3`, tv: calc.teamYr, ev: calc.entYr },
                ].map((r) => (
                  <tr key={r.y} className="border-b border-border/60">
                    <td className="py-2.5 pr-4">
                      {r.y}
                      {r.note ? (
                        <span className="ml-2 text-[11px] text-neutral-muted">
                          {r.note}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2.5 pr-4 font-medium">{fmt(r.tv)}</td>
                    <td className="py-2.5 font-medium">{fmt(r.ev)}</td>
                  </tr>
                ))}
                <tr className="font-bold text-neutral-emphasis">
                  <td className="py-3 pr-4">{t.total}</td>
                  <td className="py-3 pr-4 text-primary">{fmt(calc.team3)}</td>
                  <td className="py-3 text-primary">{fmt(calc.ent3)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Assumptions */}
      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-semibold text-neutral-emphasis">
            {t.assumptionsTitle}
          </h2>
          <ul className="space-y-2">
            {t.assumptions.map((a) => (
              <li
                key={a}
                className="flex items-start gap-3 text-sm leading-relaxed text-neutral-muted"
              >
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                  aria-hidden
                />
                <span>{a}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 border-t border-border pt-4 text-sm text-neutral-muted">
            <Link
              href={getLocalizedPathname(
                "/blog/claude-ai-pricing-malaysia-2026/",
                locale,
              )}
              className="inline-flex items-center gap-1 font-semibold text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t.pricingBlog}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
            {/* The calculator answers "what will it cost" and previously linked
                only to another pricing page, keeping the reader inside the
                pricing loop. This is the route to the commercial answer. */}
            <Link
              href={getLocalizedPathname(
                "/services/claude-managed-service-malaysia/",
                locale,
              )}
              className="mt-2 inline-flex items-center gap-1 font-semibold text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t.managedService}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
            {/* The missing rung. The link above routes to who runs Claude; this
                one answers the question this page actually provokes — the reader
                has just priced seats and tokens, and the next unknown is what the
                build costs. /blog/ai-agent-cost-malaysia/ answers exactly that and
                carries its own links into /services/ai-agents/, so the ladder runs
                "what does Claude cost" → "what does an agent cost to build" → who
                builds it. Agent development is the priority service and this page
                is the largest audience on the site (23.9k impressions/28d). */}
            <Link
              href={getLocalizedPathname(
                "/blog/ai-agent-cost-malaysia/",
                locale,
              )}
              className="mt-2 inline-flex items-center gap-1 font-semibold text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t.agentCost}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
            <Link
              href={getLocalizedPathname(
                "/blog/connect-ai-to-business-systems-mcp/",
                locale,
              )}
              className="mt-2 inline-flex items-center gap-1 font-semibold text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t.mcpLink}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </p>
        </div>
      </section>

      {/* Internal link to the AI governance hub. */}
      <section className="bg-background px-4 py-10 sm:px-6 lg:px-8">
        <GovernanceCallout locale={locale} placement="claude_cost_calculator" />
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <h2 className="mb-6 font-display text-2xl font-bold text-neutral-emphasis">
          {t.faqTitle}
        </h2>
        <div className="space-y-3">
          {t.faqs.map((f) => (
            <details
              key={f.q}
              className="group rounded-xl border border-border bg-card p-5"
            >
              <summary className="cursor-pointer list-none font-semibold text-neutral-emphasis">
                {f.q}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-neutral-muted">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-neutral-surface">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-border bg-card p-8 sm:flex-row sm:items-center">
            <div>
              <h2 className="font-display text-2xl font-semibold text-neutral-emphasis">
                {t.ctaTitle}
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-muted">
                {t.ctaDesc}
              </p>
            </div>
            <a
              href={whatsappHref(
                "Hi Anchor Sprint, I used the Claude TCO calculator and want an Enterprise quote / rollout plan.",
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-track-event="contact_whatsapp_click"
              data-track-location="claude_cost_calculator_cta"
              data-track-phone={WHATSAPP}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              <FaWhatsapp className="h-4 w-4" aria-hidden />
              {t.ctaBtn}
            </a>
          </div>
        </div>
      </section>

      {/* EzyChat (our own free WhatsApp inbox). Deliberately LAST: after the
          lead-gated Excel download and the closing CTA. Our leads first. */}
      <section className="bg-neutral-surface">
        <div className="mx-auto max-w-6xl px-4 pb-14 sm:px-6 lg:px-8">
          <EzyChatCard copy="claude" location="claude_calculator" locale={locale} />
        </div>
      </section>
    </div>
  );
}
